import { z } from 'zod';
import { lenientString, lenientUuid, lenientOptionalString, lenientDateString, lenientEnum, lenientRequiredNode, lenientNodeArray } from '../schema-extensions';
import { LiabilityType } from '../enums/liability-type.enum';
import { RateHistoryNode } from './rate-history-node-schema';
import { MonthlyChargeNode } from './monthly-charge-node-schema';
import { LiabilityReferenceNode } from './liability-reference-node-schema';

// ============================================================================
// CONSTANTS (must be defined before schemas that reference them)
// ============================================================================
const LIABILITY_CONSTANTS = {
  PROVIDER_MAX_LENGTH: 100,
  SORT_CODE_LENGTH: 6,
  ACCOUNT_NUMBER_MAX_LENGTH: 20,
  NAME_MAX_LENGTH: 100,
  LOAN_TOTAL_MAX_LENGTH: 20, // String for precision
  DATE_FORMAT_LENGTH: 10, // 'YYYY-MM-DD'
  NOTES_MAX_LENGTH: 1000,
};

// ============================================================================
// SCHEMAS
// ============================================================================

/**
 * Lenient schema for liability - accepts various formats and coerces to standard
 */
const lenientLiabilityNodeSchema = z.object({
  id: lenientUuid(),
  type: lenientEnum(LiabilityType, LiabilityType.PERSONAL_LOAN),
  target: lenientNodeArray(LiabilityReferenceNode.deserialize),
  provider: lenientString(LIABILITY_CONSTANTS.PROVIDER_MAX_LENGTH),
  sortCode: lenientOptionalString(LIABILITY_CONSTANTS.SORT_CODE_LENGTH).optional(),
  accountNumber: lenientOptionalString(LIABILITY_CONSTANTS.ACCOUNT_NUMBER_MAX_LENGTH).optional(),
  name: lenientString(LIABILITY_CONSTANTS.NAME_MAX_LENGTH),
  loanTotal: lenientString(LIABILITY_CONSTANTS.LOAN_TOTAL_MAX_LENGTH),
  loanDate: lenientDateString(''),
  repaymentDate: lenientDateString(''),
  rates: lenientRequiredNode(RateHistoryNode),
  charges: lenientNodeArray(MonthlyChargeNode.deserialize),
  notes: lenientString(LIABILITY_CONSTANTS.NOTES_MAX_LENGTH),
}).transform(val => ({
  id: val.id,
  type: val.type,
  target: val.target,
  provider: val.provider,
  sortCode: val.sortCode,
  accountNumber: val.accountNumber,
  name: val.name,
  loanTotal: val.loanTotal,
  loanDate: val.loanDate,
  repaymentDate: val.repaymentDate,
  rates: val.rates,
  charges: val.charges,
  notes: val.notes
}));

/**
 * Strict schema for liability - enforces exact format
 */
const strictLiabilityNodeSchema = z.object({
  id: z.string().min(1),
  type: z.nativeEnum(LiabilityType),
  target: z.array(z.unknown().transform(val => LiabilityReferenceNode.deserialize(val))),
  provider: z.string().max(LIABILITY_CONSTANTS.PROVIDER_MAX_LENGTH),
  sortCode: z.string().max(LIABILITY_CONSTANTS.SORT_CODE_LENGTH).optional(),
  accountNumber: z.string().max(LIABILITY_CONSTANTS.ACCOUNT_NUMBER_MAX_LENGTH).optional(),
  name: z.string().max(LIABILITY_CONSTANTS.NAME_MAX_LENGTH),
  loanTotal: z.string().max(LIABILITY_CONSTANTS.LOAN_TOTAL_MAX_LENGTH),
  loanDate: z.string().length(LIABILITY_CONSTANTS.DATE_FORMAT_LENGTH),
  repaymentDate: z.string().length(LIABILITY_CONSTANTS.DATE_FORMAT_LENGTH),
  rates: z.unknown().transform(val => RateHistoryNode.deserialize(val)),
  charges: z.array(z.unknown().transform(val => MonthlyChargeNode.deserialize(val))),
  notes: z.string().max(LIABILITY_CONSTANTS.NOTES_MAX_LENGTH),
}).transform(val => ({
  id: val.id,
  type: val.type,
  target: val.target,
  provider: val.provider,
  sortCode: val.sortCode,
  accountNumber: val.accountNumber,
  name: val.name,
  loanTotal: val.loanTotal,
  loanDate: val.loanDate,
  repaymentDate: val.repaymentDate,
  rates: val.rates,
  charges: val.charges,
  notes: val.notes
}));

// ============================================================================
// INTERFACE
// ============================================================================

export interface LiabilityNode {
  id: string;
  type: LiabilityType;
  target: LiabilityReferenceNode[];
  provider: string;
  sortCode?: string;
  accountNumber?: string;
  name: string;
  loanTotal: string;
  loanDate: string;
  repaymentDate: string;
  rates: RateHistoryNode;
  charges: MonthlyChargeNode[];
  notes: string;
}

// ============================================================================
// NAMESPACE WITH METHODS
// ============================================================================

export namespace LiabilityNode {
  /**
   * Safely deserialize unknown data into LiabilityNode
   * Uses lenient parsing to handle various input formats
   */
  export function deserialize(data: unknown): LiabilityNode {
    return lenientLiabilityNodeSchema.parse(data);
  }

  /**
   * Create a new LiabilityNode with validation
   * Uses strict parsing to ensure data integrity
   */
  export function create(data: LiabilityNode): LiabilityNode {
    return strictLiabilityNodeSchema.parse(data);
  }

  /**
   * Validate that data conforms to LiabilityNode structure
   */
  export function validate(data: unknown): data is LiabilityNode {
    return strictLiabilityNodeSchema.safeParse(data).success;
  }

  /**
   * Get the current interest rate
   */
  export function getCurrentRate(liability: LiabilityNode): number {
    return RateHistoryNode.getCurrentRate(liability.rates).rate;
  }

  /**
   * Get the total of all monthly charges
   */
  export function getTotalMonthlyCharges(liability: LiabilityNode): number {
    return liability.charges.reduce((sum, charge) => sum + charge.amount, 0);
  }

  /**
   * Check if liability has account details
   */
  export function hasAccountDetails(liability: LiabilityNode): boolean {
    return !!(liability.sortCode && liability.accountNumber);
  }

  export function isActive(liability: LiabilityNode, referenceDate: Date = new Date()): boolean {
    return !liability.repaymentDate || new Date(liability.repaymentDate) > referenceDate;
  }

  /**
   * Constants for validation and UI
   */
  export const Constants = LIABILITY_CONSTANTS;
}
